import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../../components/header/Header';
import { MobileFooter } from '../../components/mobileFooter/MobileFooter';
import './Blog.css';

interface BlogPost {
	id: string;
	title: string;
	date: string;
	category: string;
	excerpt: string;
	pinned?: boolean;
}

const BlogPage = () => {
	const [posts, setPosts] = useState<BlogPost[]>([]);
	const [loading, setLoading] = useState(true);
	const [activeCategory, setActiveCategory] = useState<string>('Alle');

	useEffect(() => {
		const loadPosts = async () => {
			try {
				const response = await fetch('/blog-posts/index.json');
				if (response.ok) {
					const data: BlogPost[] = await response.json();
					const sorted = [...data].sort((a, b) => {
						const aPinned = a.pinned ? 1 : 0;
						const bPinned = b.pinned ? 1 : 0;

						if (aPinned !== bPinned) {
							return bPinned - aPinned;
						}

						return new Date(b.date).getTime() - new Date(a.date).getTime();
					});
					setPosts(sorted);
				}
			} catch (error) {
				console.error('Error loading blog posts:', error);
			} finally {
				setLoading(false);
			}
		};
		loadPosts();
	}, []);

	const categories = ['Alle', ...Array.from(new Set(posts.map((p) => p.category)))];

	const filteredPosts =
		activeCategory === 'Alle' ? posts : posts.filter((p) => p.category === activeCategory);

	const formatDate = (dateStr: string) =>
		new Date(dateStr).toLocaleDateString('de-DE', {
			year: 'numeric',
			month: 'long',
			day: 'numeric',
		});

	return (
		<div>
			<Header style={{ backgroundColor: '#ffffff' }} />
			<div className='blog-page'>
				<div className='blog-container'>
					<div className='blog-header'>
						<h1 className='blog-title'>Neuigkeiten</h1>
						<p className='blog-subtitle'>
							Der Erledix-Blog ist unser Ort, an dem wir dich regelmäßig über
							Neuigkeiten, Updates und Entwicklungen rund um Erledix informieren.
						</p>
					</div>

					{!loading && categories.length > 1 && (
						<div className='blog-filter'>
							{categories.map((cat) => (
								<button
									key={cat}
									className={`filter-btn${activeCategory === cat ? ' filter-btn--active' : ''}`}
									onClick={() => setActiveCategory(cat)}
								>
									{cat}
								</button>
							))}
						</div>
					)}

					{loading ? (
						<div className='blog-loading'>
							<div className='blog-loading-spinner' />
							<p>Beiträge werden geladen…</p>
						</div>
					) : filteredPosts.length === 0 ? (
						<p className='blog-empty'>Keine Beiträge in dieser Kategorie.</p>
					) : (
						<div className='blog-grid'>
							{filteredPosts.map((post) => (
								<Link
									key={post.id}
									to={`/blog/${post.id}`}
									className={`blog-card${post.pinned ? ' blog-card--pinned' : ''}`}
								>
									{post.pinned && (
										<div className='blog-card__pinned'>
											<span className='blog-card__pinned-icon'>📌</span>
											<span>Angepinnt</span>
										</div>
									)}

									<div className='blog-card__meta'>
										<span
											className='blog-card__category'
											style={{
												backgroundColor:
													post.category === 'Ankündigung'
														? '#FF7675'
														: post.category === 'Einblicke'
															? '#00B894'
															: post.category === 'Entwicklung'
																? '#74B9FF'
																: '#6C5CE7',
											}}
										>
											{post.category}
										</span>
										<span className='blog-card__date'>
											{formatDate(post.date)}
										</span>
									</div>

									<h2 className='blog-card__title'>{post.title}</h2>
									<p className='blog-card__excerpt'>{post.excerpt}</p>
									<span className='blog-card__read-more'>Weiterlesen</span>
								</Link>
							))}
						</div>
					)}
				</div>
			</div>
			<MobileFooter />
		</div>
	);
};

export default BlogPage;
